import { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  Link2,
  Unlink,
  RefreshCw,
  Settings2,
  Check,
  AlertCircle,
  ExternalLink,
  Loader2,
  Clock,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Feature flag - Google OAuth verification approved
const AUTOMATIC_SYNC_ENABLED = true;

const GoogleCalendarSync = ({ onSyncComplete }) => {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  
  const [settings, setSettings] = useState({
    sync_enabled: true,
    import_events: true,
    export_bookings: true,
    selected_calendar_id: "primary",
    sync_days_ahead: 90
  });

  useEffect(() => {
    fetchStatus();
    
    // Check URL for connection result
    const urlParams = new URLSearchParams(window.location.search);
    const gcalParam = urlParams.get("gcal");
    const errorParam = urlParams.get("error");
    
    if (gcalParam === "connected") {
      toast.success("Google Calendar connected successfully!");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus();
    } else if (errorParam) {
      const errorMessages = {
        oauth_denied: "Calendar access was denied",
        missing_params: "OAuth callback missing parameters",
        invalid_state: "Invalid OAuth state",
        state_expired: "OAuth session expired, please try again",
        token_exchange_failed: "Failed to connect to Google",
        token_exchange_error: "Error connecting to Google"
      };
      toast.error(errorMessages[errorParam] || "Failed to connect Google Calendar");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`, { withCredentials: true });
      setStatus(response.data);
      if (response.data.sync_settings) {
        setSettings(response.data.sync_settings);
      }
      
      // Fetch calendars if connected
      if (response.data.connected) {
        fetchCalendars();
      }
    } catch (error) {
      console.error("Error fetching calendar status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API}/calendar/calendars`, { withCredentials: true });
      const calendarList = response.data;
      setCalendars(calendarList);
      
      // Auto-select primary calendar if no calendar is currently selected or still default "primary"
      if ((!settings.selected_calendar_id || settings.selected_calendar_id === "primary") && calendarList.length > 0) {
        const primaryCalendar = calendarList.find(c => c.primary);
        const defaultCalendar = primaryCalendar || calendarList[0];
        
        if (defaultCalendar) {
          const newSettings = { ...settings, selected_calendar_id: defaultCalendar.id };
          setSettings(newSettings);
          // Save to backend
          try {
            await axios.put(`${API}/calendar/settings`, newSettings, { withCredentials: true });
          } catch (e) {
            console.error("Failed to save default calendar selection:", e);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching calendars:", error);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await axios.get(`${API}/calendar/connect`, { withCredentials: true });
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start connection");
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect`, {}, { withCredentials: true });
      toast.success("Google Calendar disconnected");
      setStatus({ connected: false });
      setDisconnectConfirm(false);
      setCalendars([]);
      setSettings(prev => ({ ...prev, selected_calendar_id: "primary" }));
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleSync = async () => {
    // Check if calendars are loaded and a valid calendar is selected
    if (calendars.length > 0 && (!settings.selected_calendar_id || settings.selected_calendar_id === "primary")) {
      toast.error("Please select a calendar to sync in Settings first");
      setSettingsOpen(true);
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/calendar/sync`, {}, { withCredentials: true });
      const { imported_events, exported_bookings, errors } = response.data;
      
      if (errors?.length) {
        toast.warning(`Sync completed with errors: ${errors.join(", ")}`);
      } else if (imported_events > 0 || exported_bookings > 0) {
        toast.success(`Synced ${imported_events} events imported, ${exported_bookings} bookings exported`);
      } else {
        toast.success("Calendar is up to date");
      }
      
      fetchStatus();
      if (onSyncComplete) onSyncComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/calendar/settings`, settings, { withCredentials: true });
      toast.success("Settings saved");
      setSettingsOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "Never";
    return new Date(isoString).toLocaleString();
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border-border" data-testid="google-calendar-sync">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status.connected ? 'bg-green-500/10' : 'bg-muted'}`}>
                <CalendarIcon className={`w-5 h-5 ${status.connected ? 'text-green-500' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="text-lg font-serif">Google Calendar</CardTitle>
                <CardDescription>
                  {status.connected 
                    ? `Connected as ${status.google_email}`
                    : "Sync your calendar to prevent double bookings"
                  }
                </CardDescription>
              </div>
            </div>
            {status.connected && (
              <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                <Check className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Show different UI based on whether automatic sync is enabled */}
          {AUTOMATIC_SYNC_ENABLED ? (
            // Full automatic sync UI (when OAuth verification is complete)
            status.connected ? (
              <>
                {/* Sync Status */}
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">Last Synced</p>
                    <p className="text-xs text-muted-foreground">{formatDate(status.last_sync_at)}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSync}
                    disabled={syncing}
                    data-testid="sync-calendar-btn"
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {syncing ? "Syncing..." : "Sync Now"}
                  </Button>
                </div>

                {/* Quick Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">Import Events</span>
                    </div>
                    <Switch 
                      checked={settings.import_events}
                      onCheckedChange={(checked) => {
                        const newSettings = { ...settings, import_events: checked };
                        setSettings(newSettings);
                        axios.put(`${API}/calendar/settings`, newSettings, { withCredentials: true });
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">Export Bookings</span>
                    </div>
                    <Switch 
                      checked={settings.export_bookings}
                      onCheckedChange={(checked) => {
                        const newSettings = { ...settings, export_bookings: checked };
                        setSettings(newSettings);
                        axios.put(`${API}/calendar/settings`, newSettings, { withCredentials: true });
                      }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSettingsOpen(true)}
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-400 hover:text-red-300"
                    onClick={() => setDisconnectConfirm(true)}
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Benefits */}
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Automatically block time from your personal calendar</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Add confirmed bookings to your calendar</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Prevent double bookings</span>
                  </div>
                </div>

                {/* Connect Button */}
                <Button 
                  onClick={handleConnect}
                  className="w-full"
                  data-testid="connect-google-calendar-btn"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Google Calendar
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  You&apos;ll be redirected to Google to authorize access
                </p>
              </>
            )
          ) : (
            // Coming Soon UI (while OAuth verification is pending)
            <>
              {/* Coming Soon Notice */}
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <Clock className="w-4 h-4 text-blue-400" />
                <AlertDescription className="text-blue-200">
                  <span className="font-medium">Automatic sync coming soon!</span>
                  <br />
                  <span className="text-sm text-blue-300/80">
                    Two-way calendar sync is being verified by Google. In the meantime, use the &quot;Add to Calendar&quot; button on confirmed bookings.
                  </span>
                </AlertDescription>
              </Alert>

              {/* Current Features */}
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">What&apos;s available now:</p>
                <div className="space-y-2 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Add confirmed bookings to Google Calendar with one click</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Download .ics files for Apple Calendar & Outlook</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Works from Bookings page after confirming a session</span>
                  </div>
                </div>
              </div>

              {/* Upcoming Features */}
              <div className="p-3 bg-muted/20 rounded-lg space-y-2 text-sm">
                <p className="font-medium text-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  Coming with automatic sync:
                </p>
                <ul className="text-muted-foreground space-y-1 ml-6 list-disc">
                  <li>Import events from Google Calendar as blocked time</li>
                  <li>Automatically prevent double bookings</li>
                  <li>Two-way sync for seamless scheduling</li>
                </ul>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                <Info className="w-3 h-3 inline mr-1" />
                Look for the &quot;Add to Calendar&quot; button on confirmed bookings
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Calendar Sync Settings</DialogTitle>
            <DialogDescription>
              Configure how your Google Calendar syncs with 360Lumina
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Calendar Selection */}
            <div>
              <Label className="mb-2 block">Calendar to Sync</Label>
              <Select
                value={settings.selected_calendar_id}
                onValueChange={(value) => setSettings({ ...settings, selected_calendar_id: value })}
              >
                <SelectTrigger className={!settings.selected_calendar_id || settings.selected_calendar_id === "primary" ? "border-yellow-500" : ""}>
                  <SelectValue placeholder="Select a calendar" />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      {cal.summary} {cal.primary && "(Primary)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {calendars.length > 0 && (!settings.selected_calendar_id || settings.selected_calendar_id === "primary") && (
                <p className="text-xs text-yellow-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Please select a calendar to enable syncing
                </p>
              )}
              {calendars.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No calendars found. Try disconnecting and reconnecting.
                </p>
              )}
            </div>

            {/* Sync Options */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Import Events</Label>
                  <p className="text-xs text-muted-foreground">
                    Block availability when you have events in Google Calendar
                  </p>
                </div>
                <Switch
                  checked={settings.import_events}
                  onCheckedChange={(checked) => setSettings({ ...settings, import_events: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Export Bookings</Label>
                  <p className="text-xs text-muted-foreground">
                    Add confirmed bookings to your Google Calendar
                  </p>
                </div>
                <Switch
                  checked={settings.export_bookings}
                  onCheckedChange={(checked) => setSettings({ ...settings, export_bookings: checked })}
                />
              </div>
            </div>

            {/* Sync Range */}
            <div>
              <Label className="mb-2 block">Sync Days Ahead</Label>
              <Select
                value={settings.sync_days_ahead?.toString()}
                onValueChange={(value) => setSettings({ ...settings, sync_days_ahead: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation */}
      <Dialog open={disconnectConfirm} onOpenChange={setDisconnectConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              Disconnect Google Calendar
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect your Google Calendar? This will:
            </DialogDescription>
          </DialogHeader>
          
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>Stop syncing events from Google Calendar</li>
            <li>Stop adding bookings to your Google Calendar</li>
            <li>Remove synced blocked time slots</li>
          </ul>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisconnect}>
              <Unlink className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleCalendarSync;
